import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material';
import {
  Save,
  Close,
  Add as AddIcon,
} from '@mui/icons-material';

const ApartmentForm = () => {
  const history = useHistory();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);

  // Amenities options
  const amenitiesOptions = [
    'Swimming Pool',
    'Gym',
    'Kids Play Area',
    'Clubhouse',
    'Garden',
    'Jogging Track',
    'Indoor Games',
    'Party Hall',
    'Spa',
    '24/7 Security',
    'CCTV Surveillance',
    'Power Backup',
    'Elevator/Lift',
    'Parking',
    'Rainwater Harvesting',
    'Solar Panels',
    'Community Center',
    'Shopping Center',
    'Temple',
    'Cafeteria',
    'Library',
    'Tennis Court',
    'Badminton Court',
    'Basketball Court',
    'Amphitheater',
    'Senior Citizen Park',
    'Meditation Area',
    'Yoga Room',
  ];

  const [formData, setFormData] = useState({
    apartmentId: '',
    badge: '2 BHK',
    name: '',
    developer: '',
    type: 'Residential',
    email: '',
    phone: '',
    city: '',
    state: '',
    startDate: '',
    completeDate: '',
    contactPerson: '',
    reraNo: '',
    totalUnits: '',
    guidelineValue: '',
    possessionDate: '',
    address: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    amenities: [],
    configuration: '2 BHK',
    carpetArea: '',
    floor: '',
    parking: '',
    status: 'Available',
  });

  useEffect(() => {
    if (location.state && location.state.apartment) {
      setIsEditMode(true);
      const apartment = location.state.apartment;
      setSelectedApartment(apartment);
      
      // Parse dates from the format "DD/MM/YYYY | YYYY-MM-DD"
      const parseDate = (dateStr) => {
        if (!dateStr || dateStr === 'TBD | TBD') return '';
        const parts = dateStr.split('|');
        return parts.length > 1 ? parts[1].trim() : '';
      };

      setFormData({
        apartmentId: apartment.apartmentId || '',
        badge: apartment.badge || '2 BHK',
        name: apartment.name || '',
        developer: apartment.developer || '',
        type: apartment.type || 'Residential',
        email: apartment.email || '',
        phone: apartment.phone || '',
        city: apartment.city || '',
        state: apartment.state || '',
        startDate: parseDate(apartment.startDate) || '',
        completeDate: parseDate(apartment.completeDate) || '',
        contactPerson: apartment.contactPerson || '',
        reraNo: apartment.reraNo || '',
        totalUnits: apartment.totalUnits || '',
        guidelineValue: apartment.guidelineValue || '',
        possessionDate: apartment.possessionDate || '',
        address: apartment.address || '',
        bankName: apartment.bankName || '',
        accountNumber: apartment.accountNumber || '',
        ifscCode: apartment.ifscCode || '',
        amenities: apartment.amenities ? (typeof apartment.amenities === 'string' ? apartment.amenities.split(', ') : apartment.amenities) : [],
        configuration: apartment.configuration || '2 BHK',
        carpetArea: apartment.carpetArea || '',
        floor: apartment.floor || '',
        parking: apartment.parking || '',
        status: apartment.status || 'Available',
      });
    } else {
      setIsEditMode(false);
      setSelectedApartment(null);
      setFormData({
        apartmentId: '',
        badge: '2 BHK',
        name: '',
        developer: '',
        type: 'Residential',
        email: '',
        phone: '',
        city: '',
        state: '',
        startDate: '',
        completeDate: '',
        contactPerson: '',
        reraNo: '',
        totalUnits: '',
        guidelineValue: '',
        possessionDate: '',
        address: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        amenities: [],
        configuration: '2 BHK',
        carpetArea: '',
        floor: '',
        parking: '',
        status: 'Available',
      });
    }
  }, [location.state]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = () => {
    if (!formData.apartmentId || !formData.name || !formData.developer) {
      alert('Please fill in all required fields (Apartment ID, Name, Developer)');
      return;
    }
    
    if (isEditMode) {
      alert(`Apartment ${selectedApartment.apartmentId} updated successfully!`);
    } else {
      alert('New apartment added successfully!');
    }
    
    history.push('/app/project/ProjectPricing');
  };

  const handleFormClose = () => {
    history.push('/app/project/ProjectPricing');
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Form Page Header */}
      <Box sx={{ mb: 4, pb: 3, borderBottom: '2px solid #e0e0e0', backgroundColor: '#fff', p: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              {isEditMode ? 'Edit Apartment' : 'Add New Apartment'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode 
                ? `Update apartment information for ${selectedApartment?.apartmentId}` 
                : 'Fill in the details to add a new apartment'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={handleFormClose}
            sx={{ textTransform: 'none', px: 3 }}
          >
            Back to List
          </Button>
        </Box>
      </Box>

      {/* Form Content */}
      <Paper sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Apartment ID"
              value={formData.apartmentId}
              onChange={(e) => handleFormChange('apartmentId', e.target.value)}
              placeholder="APT001"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Apartment Name"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="Enter apartment name"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Developer Name"
              value={formData.developer}
              onChange={(e) => handleFormChange('developer', e.target.value)}
              placeholder="Developer company name"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Apartment Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleFormChange('type', e.target.value)}
                label="Apartment Type"
              >
                <MenuItem value="Residential">Residential</MenuItem>
                <MenuItem value="Luxury">Luxury</MenuItem>
                <MenuItem value="Budget">Budget</MenuItem>
                <MenuItem value="Premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Configuration</InputLabel>
              <Select
                value={formData.configuration}
                onChange={(e) => {
                  handleFormChange('configuration', e.target.value);
                  handleFormChange('badge', e.target.value);
                }}
                label="Configuration"
              >
                <MenuItem value="1 BHK">1 BHK</MenuItem>
                <MenuItem value="2 BHK">2 BHK</MenuItem>
                <MenuItem value="3 BHK">3 BHK</MenuItem>
                <MenuItem value="4 BHK">4 BHK</MenuItem>
                <MenuItem value="Penthouse">Penthouse</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleFormChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="Under Construction">Under Construction</MenuItem>
                <MenuItem value="Available">Available</MenuItem>
                <MenuItem value="Hold">Hold</MenuItem>
                <MenuItem value="Booked">Booked</MenuItem>
                <MenuItem value="Mortgage">Mortgage</MenuItem>
                <MenuItem value="Registry">Registry</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Amenities */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel 
                sx={{ 
                  fontSize: '0.95rem',
                  '&.Mui-focused': { color: '#536DFE' }
                }}
              >
                Select Amenities
              </InputLabel>
              <Select
                multiple
                value={formData.amenities}
                onChange={(e) => handleFormChange('amenities', e.target.value)}
                input={<OutlinedInput label="Select Amenities" />}
                renderValue={(selected) => (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 0.75,
                    py: 0.75 
                  }}>
                    {selected.length === 0 ? (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#95a5a6',
                          fontStyle: 'italic',
                          fontSize: '0.9rem'
                        }}
                      >
                        No amenities selected
                      </Typography>
                    ) : (
                      selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value} 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#E8F4F8',
                            color: '#2C5F7F',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            height: 26,
                            border: '1px solid #D4E9F2',
                            '&:hover': {
                              backgroundColor: '#D4E9F2'
                            }
                          }}
                        />
                      ))
                    )}
                  </Box>
                )}
                sx={{
                  '& .MuiSelect-select': {
                    minHeight: 'auto',
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#536DFE',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#536DFE',
                    borderWidth: '2px',
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 320,
                      width: 340,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      borderRadius: '8px',
                      mt: 1,
                      '& .MuiMenuItem-root': {
                        px: 2.5,
                        py: 1.25,
                        fontSize: '0.9rem',
                        '&:hover': {
                          backgroundColor: '#F5F7FA',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#E8F4F8',
                          '&:hover': {
                            backgroundColor: '#D4E9F2',
                          }
                        }
                      },
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  variant: 'menu',
                }}
              >
                {amenitiesOptions.map((amenity) => (
                  <MenuItem key={amenity} value={amenity}>
                    <Checkbox 
                      checked={formData.amenities.indexOf(amenity) > -1}
                      sx={{
                        color: '#B0BEC5',
                        '&.Mui-checked': {
                          color: '#536DFE',
                        }
                      }}
                    />
                    <ListItemText 
                      primary={amenity}
                      primaryTypographyProps={{
                        fontWeight: formData.amenities.indexOf(amenity) > -1 ? 500 : 400,
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Specifications */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Specifications
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Carpet Area"
              value={formData.carpetArea}
              onChange={(e) => handleFormChange('carpetArea', e.target.value)}
              placeholder="e.g., 1200 sq.ft"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Floor Range"
              value={formData.floor}
              onChange={(e) => handleFormChange('floor', e.target.value)}
              placeholder="e.g., 5th to 15th"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Parking"
              value={formData.parking}
              onChange={(e) => handleFormChange('parking', e.target.value)}
              placeholder="e.g., 1 Covered + 1 Open"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Total Units"
              type="number"
              value={formData.totalUnits}
              onChange={(e) => handleFormChange('totalUnits', e.target.value)}
              placeholder="0"
            />
          </Grid>

          {/* Contact Information */}
          {/* <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Contact Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact Person"
              value={formData.contactPerson}
              onChange={(e) => handleFormChange('contactPerson', e.target.value)}
              placeholder="Enter contact person name"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              placeholder="email@example.com"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              placeholder="+91 9876543210"
            />
          </Grid> */}

          {/* Project Details */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Project Details
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={formData.startDate}
              onChange={(e) => handleFormChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Completion Date"
              value={formData.completeDate}
              onChange={(e) => handleFormChange('completeDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="RERA Number"
              value={formData.reraNo}
              onChange={(e) => handleFormChange('reraNo', e.target.value)}
              placeholder="Enter RERA number"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Circle Rate"
              value={formData.guidelineValue}
              onChange={(e) => handleFormChange('guidelineValue', e.target.value)}
              placeholder="₹0.00"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Possession Date"
              value={formData.possessionDate}
              onChange={(e) => handleFormChange('possessionDate', e.target.value)}
              placeholder="e.g. Dec 2024"
            />
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Address Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Full Address"
              value={formData.address}
              onChange={(e) => handleFormChange('address', e.target.value)}
              placeholder="Enter complete address"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => handleFormChange('city', e.target.value)}
              placeholder="Enter city"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="State"
              value={formData.state}
              onChange={(e) => handleFormChange('state', e.target.value)}
              placeholder="Enter state"
            />
          </Grid>

          {/* Bank Details */}
          {/* <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Bank Details
            </Typography>
          </Grid> */}

          {/* <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Bank Name"
              value={formData.bankName}
              onChange={(e) => handleFormChange('bankName', e.target.value)}
              placeholder="Enter bank name"
            />
          </Grid> */}

          {/* <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Account Number"
              value={formData.accountNumber}
              onChange={(e) => handleFormChange('accountNumber', e.target.value)}
              placeholder="Enter account number"
            />
          </Grid> */}

          {/* <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="IFSC Code"
              value={formData.ifscCode}
              onChange={(e) => handleFormChange('ifscCode', e.target.value)}
              placeholder="Enter IFSC code"
            />
          </Grid> */}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={handleFormClose}
                variant="outlined"
                startIcon={<Close />}
                sx={{ textTransform: 'none', px: 4, py: 1.2 }}
                size="large"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFormSubmit}
                variant="contained"
                color="primary"
                startIcon={isEditMode ? <Save /> : <AddIcon />}
                sx={{
                  textTransform: 'none',
                  px: 4,
                  py: 1.2,
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                }}
                size="large"
              >
                {isEditMode ? 'Update Apartment' : 'Add Apartment'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ApartmentForm;

